import React, { useEffect, useState } from "react";
import { Alert, Spinner, Button } from "react-bootstrap";
import { EyeIcon } from "../../images/icons/icons";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import ReopenTicketModal from "./ReopenTicketModal";
import RejectTicketModal from "./RejectTicketModal";
import HandoverTicketModal from "./HandoverTicketModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchSupervisors } from "../../redux/reducers/ticketSystemSlice";
import { selectToken } from "../auth/auth.selector";
import "../../styles/ticket-system/fd-dashboard.css";
import "../../styles/ticket-system/ticket-status-badges.css";
import "../../styles/ticket-system/mobile-ticket-listing.css";
import { formatDateTime, formatTicketDate } from "../common-data/Converts";
import { replaceUnderscores } from "./utils/formatStatus";
import { TicketRejectUserAccess } from "./utils/ticketActionUserAccess";
import ReadMoreDescription from "../common-components/ReadMoreDescription";
import {
  TICKET_CLOSED,
  TICKET_OPENED,
  TICKET_REJECTED_BY_MANAGER,
  TICKET_REJECTED_BY_RECEPTIONIST,
  TICKET_REOPENED,
  TICKET_WORK_COMPLETED,
} from "./utils/ticketStatus";
import TicketLifecycleTimeline from "./TicketLifecycleTimeline";

// Mobile Ticket Card Component
const MobileTicketCard = ({
  ticket,
  onTicketClick,
  onVerifyClick,
  onMarkAsResolved,
  onReopenTicket,
  onRejectTicket,
  userInfoData,
  isManagerAssignment = false,
  onTimelineClick,
  canUserRejectTicket,
}) => {
  // Check if user can reopen ticket
  const canReopenTicket = (ticket) => {
    const reopenableStatuses = ["rejected", "rejected_by_mgr"];
    return reopenableStatuses.includes(ticket.status);
  };

  const handleReopenTicket = (ticket) => {
    if (onReopenTicket) {
      onReopenTicket(ticket);
    }
  };

  return (
    <div className="mobile-ticket-card" onClick={() => onTicketClick(ticket)}>
      {/* Card Header */}
      <div className="mobile-card-header">
        <div className="ticket-header-left">
          <div className="mobile-ticket-number">
            <span className="ticket-id">{ticket.ticket_no}</span>
            <CompactSLADisplay ticket={ticket} />
          </div>
          <div className="mobile-badges">
            <span
              className={`mobile-priority-badge priority-${ticket.priority?.toLowerCase()}`}
            >
              {ticket.priority?.toUpperCase()}
            </span>
            {ticket?.resolution_type && (
              <span
                className={`mobile-resolution-badge resolution-${ticket.resolution_type?.toLowerCase()}`}
              >
                {ticket.resolution_type}
              </span>
            )}
          </div>
        </div>
        <div className="ticket-header-right">
          <span
            className={`mobile-status-badge ticket-system-status-badge status-${ticket.status?.toLowerCase()}`}
          >
            {replaceUnderscores(ticket.status)}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="mobile-card-content">
        {/* Raised By Section */}
        <div className="mobile-info-section">
          <div className="mobile-info-item">
            <span className="mobile-label">Raised By</span>
            <div className="mobile-user-info">
              <span className="mobile-user-name">
                {ticket?.created_by_name}
              </span>
              <span className="mobile-user-contact">
                {ticket?.created_by_mobile}
              </span>
              <span className="mobile-date">
                {formatDateTime(ticket?.created_date)}
              </span>
            </div>
          </div>

          {/* Assigned To Section - Only for regular listing */}
          {!isManagerAssignment && (
            <div className="mobile-info-item">
              <span className="mobile-label">Assigned To</span>
              <div className="mobile-user-info">
                {(() => {
                  const hasAssignedTo =
                    ticket?.assigned_to && ticket.assigned_to.trim() !== "";
                  const hasAssignedToName =
                    ticket?.assigned_to_name &&
                    ticket.assigned_to_name.trim() !== "";

                  if (hasAssignedToName) {
                    // Show the assigned user info
                    return (
                      <>
                        <span className="mobile-user-name">
                          {ticket?.assigned_to_name}
                        </span>
                        <span className="mobile-user-contact">
                          {ticket?.assigned_to_mobile}
                        </span>
                        <span className="mobile-date">
                          {formatDateTime(ticket?.assigned_date)}
                        </span>
                      </>
                    );
                  } else if (hasAssignedTo && !hasAssignedToName) {
                    // User was assigned but deleted/left
                    return (
                      <span
                        className="mobile-user-name"
                        style={{ color: "#dc3545", fontStyle: "italic" }}
                      >
                        Assigned user no longer available
                      </span>
                    );
                  } else {
                    // Not assigned yet
                    return (
                      <span className="mobile-user-name">Not Assigned Yet</span>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {/* Gym Section */}
          <div className="mobile-info-item">
            <span className="mobile-label">Gym</span>
            <span className="mobile-gym-name">{ticket.gym_name}</span>
          </div>

          {/* Last Updated Section */}
          <div className="mobile-info-item">
            <span className="mobile-label">Last Updated</span>
            <span className="mobile-date">
              {formatTicketDate(ticket?.last_updated)}
            </span>
          </div>
        </div>

        {/* Issue Section */}
        <div className="mobile-issue-section">
          <div className="mobile-category-path">
            <span className="mobile-category">{ticket.category_name}</span>
            <span className="mobile-separator">→</span>
            <span className="mobile-subcategory">
              {ticket.sub_category_name}
            </span>
          </div>
          <div className="mobile-description">
            <ReadMoreDescription
              text={ticket.description}
              maxLength={80}
              className="mobile-description-text"
            />
          </div>
        </div>
      </div>

      {/* Card Actions */}
      <div className="mobile-card-actions" onClick={(e) => e.stopPropagation()}>
        {/* Manager Assignment Action */}
        {isManagerAssignment ? (
          <>
            {/* Reject Button for Manager Assignment - Only for users with reject access and only when ticket is opened */}
            {canUserRejectTicket(ticket.status) && (
              <button
                className="mobile-action-btn mobile-reject-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onRejectTicket(ticket);
                }}
                title="Reject Ticket"
              >
                <span>Reject</span>
              </button>
            )}

            <Button
              variant="success"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onTicketClick(ticket);
              }}
              disabled={
                ticket?.status !== TICKET_OPENED &&
                ticket?.status !== TICKET_REOPENED
              }
              className="mobile-assign-btn"
            >
              View and Assign
            </Button>
          </>
        ) : (
          <>
            <button
              className="mobile-action-btn mobile-view-btn"
              onClick={(e) => {
                e.stopPropagation();
                onTicketClick(ticket);
              }}
              title="View Details"
            >
              <EyeIcon />
              <span>View</span>
            </button>

            <button
              className="mobile-action-btn mobile-timeline-btn"
              onClick={(e) => {
                e.stopPropagation();
                onTimelineClick(ticket);
              }}
              title="View Timeline"
              style={{
                backgroundColor: "#667eea",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "11px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span style={{ fontSize: "12px" }}>📊</span>
              <span>Timeline</span>
            </button>
          </>
        )}

        {/* Verify Button for Managers */}
        {userInfoData?.ticket_account_type === "manager" && onVerifyClick && (
          <button
            className="mobile-action-btn mobile-verify-btn"
            onClick={(e) => {
              e.stopPropagation();
              onVerifyClick(ticket);
            }}
            title="Review & Verify"
            disabled={ticket.status !== "resolved"}
          >
            <span> Review & Verify </span>
          </button>
        )}

        {/* Reject Button - Role-based rejection rules */}
        {canUserRejectTicket(ticket.status) && (
          <button
            className="mobile-action-btn mobile-reject-btn"
            onClick={(e) => {
              e.stopPropagation();
              onRejectTicket(ticket);
            }}
            title="Reject Ticket"
          >
            <span>Reject</span>
          </button>
        )}

        {/* Mark as Resolved for Receptionists */}
        {onMarkAsResolved &&
          userInfoData?.ticket_account_type === "receptionist" && (
            <button
              className="mobile-action-btn mobile-resolve-btn"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsResolved(ticket);
              }}
              title={
                ticket.status !== "work_completed"
                  ? "Available when work is completed"
                  : "Mark as Resolved"
              }
              disabled={ticket.status !== "work_completed"}
            >
              <span>Mark as Resolved</span>
            </button>
          )}

        {/* Reopen Button */}
        {userInfoData?.ticket_account_type !== "supervisor" && (
          <button
            className="mobile-action-btn mobile-reopen-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleReopenTicket(ticket);
            }}
            disabled={!canReopenTicket(ticket)}
            title={
              canReopenTicket(ticket)
                ? "Reopen Ticket"
                : "Cannot reopen this ticket"
            }
          >
            <span>Reopen</span>
          </button>
        )}
      </div>
    </div>
  );
};

const TicketListing = ({
  tickets,
  onTicketClick,
  onVerifyClick,
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  tablePagination = [],
  userInfoData,
  onMarkAsResolved,
  onReopenTicket,
  // Manager assignment specific props
  isManagerAssignment = false,
  managerSupervisors = [],
  managerSupervisorsLoading = false,
  selectedAssignments = {},
  setSelectedAssignments,
  assigningTickets = {},
  onAssignTicket,
  handleReload,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  // Helper function to determine assignment status
  const getAssignmentStatus = (ticket) => {
    const hasAssignedTo =
      ticket?.assigned_to && ticket.assigned_to.trim() !== "";
    const hasAssignedToName =
      ticket?.assigned_to_name && ticket.assigned_to_name.trim() !== "";

    if (!hasAssignedTo && !hasAssignedToName) {
      // First time assignment - both are null/empty
      return {
        isFirstTime: true,
        buttonText: "Assign",
        tooltipText: "Assign Ticket to Supervisor",
      };
    } else if (hasAssignedTo && !hasAssignedToName) {
      // User was assigned but deleted/left - has ID but no name
      return {
        isFirstTime: false,
        buttonText: "Reassign",
        tooltipText: "Reassign Ticket to Another Supervisor",
      };
    } else if (hasAssignedTo && hasAssignedToName) {
      // Normal reassignment - both have values
      return {
        isFirstTime: false,
        buttonText: "Reassign",
        tooltipText: "Reassign Ticket to Another Supervisor",
      };
    } else {
      // Edge case: has name but no ID (shouldn't happen normally)
      return {
        isFirstTime: true,
        buttonText: "Assign",
        tooltipText: "Assign Ticket to Supervisor",
      };
    }
  };

  const { ticketsLoading, ticketsData, ticketsPagination } = useSelector(
    (state) => state.ticketSystem
  );

  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Centralized modal state management
  const [modalState, setModalState] = useState({
    reopen: { isOpen: false, ticket: null },
    reject: { isOpen: false, ticket: null },
    handover: { isOpen: false, ticket: null },
    timeline: { isOpen: false, ticket: null },
  });

  // Helper function to open modals
  const openModal = (modalType, ticket) => {
    setModalState((prev) => ({
      ...prev,
      [modalType]: { isOpen: true, ticket },
    }));
  };

  // Helper function to close modals
  const closeModal = (modalType) => {
    setModalState((prev) => ({
      ...prev,
      [modalType]: { isOpen: false, ticket: null },
    }));
  };

  // Legacy state for backward compatibility
  const isReopenModalOpen = modalState.reopen.isOpen;
  const selectedTicketForReopen = modalState.reopen.ticket;
  const isRejectModalOpen = modalState.reject.isOpen;
  const selectedTicketForReject = modalState.reject.ticket;
  const isHandoverModalOpen = modalState.handover.isOpen;
  const selectedTicketForHandover = modalState.handover.ticket;
  const isTimelineModalOpen = modalState.timeline.isOpen;
  const selectedTicketForTimeline = modalState.timeline.ticket;

  // Updated modal handlers using centralized approach
  const handleReopenTicket = (ticket) => {
    openModal("reopen", ticket);
  };

  const handleReopenModalClose = () => {
    closeModal("reopen");
  };

  const handleReopenSubmit = () => {
    handleReopenModalClose();
    if (onReopenTicket) {
      onReopenTicket();
    }
  };

  const handleRejectTicket = (ticket) => {
    openModal("reject", ticket);
  };

  const handleRejectModalClose = () => {
    closeModal("reject");
  };

  const handleRejectSubmit = () => {
    handleRejectModalClose();
  };

  const handleHandoverTicket = (ticket) => {
    openModal("handover", ticket);
  };

  const handleHandoverModalClose = () => {
    closeModal("handover");
    if (handleReload) {
      handleReload();
    }
  };

  const handleHandoverSubmit = () => {
    handleHandoverModalClose();
    if (handleReload) {
      handleReload();
    }
  };

  const handleTimelineClick = (ticket) => {
    openModal("timeline", ticket);
  };

  const handleTimelineModalClose = () => {
    closeModal("timeline");
  };

  useEffect(() => {
    if (token) {
      dispatch(fetchSupervisors(token));
    }
  }, [dispatch, token]);

  // Check if user can reopen ticket
  const canReopenTicket = (ticket) => {
    const reopenableStatuses = ["rejected", "rejected_by_mgr"];
    return reopenableStatuses.includes(ticket.status);
  };

  // Helper function to check if user can reject ticket based on role and status
  const canUserRejectTicket = (ticketStatus) => {
    if (!TicketRejectUserAccess.includes(userInfoData?.ticket_account_type)) {
      return false;
    }

    const userRole = userInfoData?.ticket_account_type;

    if (["manager", "admin", "cluster_manager"].includes(userRole)) {
      // Manager, Admin, Cluster Manager: Can reject in all statuses except rejected, verify, closed
      const excludedStatuses = [
        "rejected",
        "verified_and_closed",
        "rejected_by_mgr",
        "resolved",
      ];
      return !excludedStatuses.includes(ticketStatus);
    } else if (userRole === "receptionist") {
      // Receptionist: Can only reject in open status
      return ticketStatus === TICKET_OPENED;
    }

    return false;
  };

  // Reusable reject button component with role-based rejection rules
  const renderRejectButton = (row) => {
    const canReject = canUserRejectTicket(row.status);
    const buttonTitle = canReject
      ? "Reject Ticket"
      : userInfoData?.ticket_account_type === "receptionist"
      ? "Can only reject open tickets"
      : "Cannot reject in this status";

    return (
      <button
        className=""
        onClick={(e) => {
          e.stopPropagation();
          if (canReject) {
            handleRejectTicket(row);
          }
        }}
        title={buttonTitle}
        style={{
          backgroundColor: canReject ? "#dc3545" : "#6c757d",
          color: "white",
          border: "none",
          borderRadius: "4px",
          padding: "6px 12px",
          fontSize: "12px",
          fontWeight: "500",
          marginLeft: "8px",
          opacity: canReject ? 1 : 0.6,
          cursor: canReject ? "pointer" : "not-allowed",
        }}
        disabled={!canReject}
      >
        Reject
      </button>
    );
  };

  // Define base columns that are common to both modes
  const baseColumns = [
    {
      dataField: "ticket_no",
      text: <b>Ticket</b>,
      formatter: (_, row) => (
        <div className="ticket-id-cell">
          <div className="ticket-id-wrapper">
            <span className="ticket-id-text">{row.ticket_no}</span>
          </div>
          {/* <br /> */}
          <div className="align-center" style={{ gap: "5px" }}>
            <span
              className={`ticket-system-status-badge status-${row.priority?.toLowerCase()}`}
            >
              {row.priority?.toUpperCase()}
            </span>
            {row?.resolution_type && (
              <span
                className={`ticket-resolution-type resolution-${row.resolution_type?.toLowerCase()}`}
              >
                {row.resolution_type}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      dataField: "created_by_name",
      text: <b>Raised By</b>,
      formatter: (_, row) => (
        <div className="raised-by-cell align-center-column">
          {row?.created_by_name ? (
            <>
              <span className="user-name">{row?.created_by_name}</span>
              <small className="user-role">{row?.created_by_mobile}</small>
              <small className="user-role" style={{ fontWeight: "600" }}>
                {formatDateTime(row?.created_date)}
              </small>
            </>
          ) : (
            <span className="user-name">
              <Button
                variant="warning"
                size="sm"
                className="handover-ticket-btn"
                title="User is deleted from the system kindly handover the ticket ownership to another user"
                onClick={(e) => {
                  e.stopPropagation();
                  handleHandoverTicket(row);
                }}
              >
                Handover Ownership
              </Button>
            </span>
          )}
        </div>
      ),
    },
    {
      dataField: "gym_name",
      text: <b>Gym</b>,
      formatter: (_, row) => (
        <div className="facility-cell align-center-column">
          <span className="facility-name">{row.gym_name}</span>
        </div>
      ),
    },
    {
      dataField: "issue",
      text: <b>Issue</b>,
      formatter: (_, row) => (
        <div className="issue-cell align-center-column">
          <div className="category-path">
            <span className="category">{row.category_name}</span>
            <span className="separator">→</span>
            <span className="subcategory">{row.sub_category_name}</span>
          </div>
          <div
            className="issue-description"
            style={{
              maxWidth: "200px",
            }}
          >
            <ReadMoreDescription
              text={row.description}
              maxLength={25}
              className=""
            />
          </div>
        </div>
      ),
    },
    {
      dataField: "last_updated",
      text: <b>Last Updated</b>,
      formatter: (_, row) => (
        <div className="last-updated-cell align-center-column">
          <small className="update-time" style={{ fontWeight: "600" }}>
            {formatTicketDate(row?.last_updated)}
          </small>
        </div>
      ),
    },
    {
      dataField: "timeline",
      text: <b>Ticket LifeCycle</b>,
      formatter: (_, row) => (
        <div
          className="underline"
          onClick={(e) => {
            e.stopPropagation();
            handleTimelineClick(row);
          }}
        >
          <span className="timeline-icon">📊</span>
          <span>Click & View</span>
        </div>
      ),
    },
  ];

  // Manager assignment specific columns
  const managerAssignmentColumns = [
    ...baseColumns,
    {
      dataField: "sla_status",
      text: <b>SLA Status</b>,
      formatter: (_, row) => (
        <div className="sla-cell align-center">
          <PremiumSLADisplay ticket={row} />
        </div>
      ),
    },
    {
      dataField: "actions",
      text: <b>Actions</b>,
      formatter: (_, row) => (
        <div className="actions-cell align-center">
          <div className="table-actions">
            <button
              className="table-action-btn view-action"
              onClick={(e) => {
                e.stopPropagation();
                onTicketClick(row);
              }}
              title="View Details"
            >
              <EyeIcon />
            </button>

            {/* Reject Button - Reusable component */}
            {renderRejectButton(row)}

            <button
              // className="table-action-btn view-action"
              onClick={(e) => {
                e.stopPropagation();
                onTicketClick(row);
              }}
              title="View and Asign Ticket to Supervisor"
              style={{
                backgroundColor:
                  row.status === TICKET_OPENED || row.status === TICKET_REOPENED
                    ? "#28a745"
                    : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: "500",
                marginLeft: "8px",
                cursor:
                  row.status === TICKET_OPENED || row.status === TICKET_REOPENED
                    ? "pointer"
                    : "not-allowed",
                opacity:
                  row.status === TICKET_OPENED || row.status === TICKET_REOPENED
                    ? 1
                    : 0.6,
              }}
              disabled={
                row.status !== TICKET_OPENED && row.status !== TICKET_REOPENED
              }
            >
              View and Assign{" "}
            </button>
          </div>
        </div>
      ),
    },
  ];

  // Regular ticket listing columns
  const regularColumns = [
    ...baseColumns.slice(0, 2), // ticket_no column

    {
      dataField: "assigned_to_name",
      text: <b>Assigned To</b>,
      formatter: (_, row) => {
        const hasAssignedTo = row?.assigned_to && row.assigned_to.trim() !== "";
        const hasAssignedToName =
          row?.assigned_to_name && row.assigned_to_name.trim() !== "";

        return (
          <div className="raised-by-cell align-center-column">
            {(() => {
              if (hasAssignedToName) {
                // Show the assigned user info
                return (
                  <>
                    <span className="user-name">{row?.assigned_to_name}</span>
                    <small className="user-role">
                      {row?.assigned_to_mobile}
                    </small>
                    <small className="user-role" style={{ fontWeight: "600" }}>
                      {formatDateTime(row?.assigned_date)}
                    </small>
                  </>
                );
              } else if (hasAssignedTo && !hasAssignedToName) {
                // User was assigned but deleted/left
                return (
                  <span
                    className="user-name"
                    style={{ color: "#dc3545", fontStyle: "italic" }}
                  >
                    Assigned user no longer available
                  </span>
                );
              } else {
                // Not assigned yet
                return <span className="user-name">Not Assigned Yet</span>;
              }
            })()}
          </div>
        );
      },
    },
    ...baseColumns.slice(2), // gym_name and issue columns

    {
      dataField: "status",
      text: <b>Status</b>,
      formatter: (_, row) => (
        <div className="status-cell align-center">
          <span
            className={`ticket-system-status-badge status-${row.status?.toLowerCase()}`}
          >
            {replaceUnderscores(row.status)}
          </span>
        </div>
      ),
    },
    {
      dataField: "sla_status",
      text: <b>SLA Status</b>,
      formatter: (_, row) => (
        <div className="sla-cell align-center">
          <PremiumSLADisplay ticket={row} />
        </div>
      ),
    },
    {
      dataField: "actions",
      text: <b>Actions</b>,
      formatter: (_, row) => (
        <div className="actions-cell align-center">
          <div className="table-actions">
            <button
              className="table-action-btn view-action"
              onClick={(e) => {
                e.stopPropagation();
                onTicketClick(row);
              }}
              title="View Details"
            >
              <EyeIcon />
            </button>

            {/* Show verify button for managers and cluster managers on resolved tickets */}
            {(userInfoData?.ticket_account_type === "manager" ||
              userInfoData?.ticket_account_type === "cluster_manager") &&
              onVerifyClick && (
                <button
                  className=""
                  onClick={(e) => {
                    e.stopPropagation();
                    onVerifyClick(row);
                  }}
                  title="Review & Verify"
                  style={{
                    backgroundColor:
                      row.status === "resolved" ? "#28a745" : "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginLeft: "8px",
                    cursor:
                      row.status === "resolved" ? "pointer" : "not-allowed",
                    opacity: row.status === "resolved" ? 1 : 0.6,
                  }}
                  disabled={row.status !== "resolved"}
                >
                  Review & Verify
                </button>
              )}

            {(userInfoData?.ticket_account_type === "manager" ||
              userInfoData?.ticket_account_type === "cluster_manager") &&
              (() => {
                const assignmentStatus = getAssignmentStatus(row);
                return (
                  <button
                    className=""
                    onClick={(e) => {
                      e.stopPropagation();
                      onVerifyClick(row);
                    }}
                    title={assignmentStatus.tooltipText}
                    style={{
                      backgroundColor: ![
                        TICKET_CLOSED,
                        TICKET_REOPENED,
                        TICKET_OPENED,
                        TICKET_REJECTED_BY_MANAGER,
                        TICKET_REJECTED_BY_RECEPTIONIST,
                      ].includes(row.status)
                        ? "#28a745"
                        : "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      fontWeight: "500",
                      marginLeft: "8px",
                      cursor: ![
                        TICKET_CLOSED,
                        TICKET_REOPENED,
                        TICKET_OPENED,
                        TICKET_REJECTED_BY_MANAGER,
                        TICKET_REJECTED_BY_RECEPTIONIST,
                      ].includes(row.status)
                        ? "pointer"
                        : "not-allowed",
                      opacity: ![
                        TICKET_CLOSED,
                        TICKET_REOPENED,
                        TICKET_OPENED,
                        TICKET_REJECTED_BY_MANAGER,
                        TICKET_REJECTED_BY_RECEPTIONIST,
                      ].includes(row.status)
                        ? 1
                        : 0.6,
                    }}
                    disabled={[
                      TICKET_CLOSED,
                      TICKET_REOPENED,
                      TICKET_OPENED,
                      TICKET_REJECTED_BY_MANAGER,
                      TICKET_REJECTED_BY_RECEPTIONIST,
                    ].includes(row.status)}
                  >
                    {assignmentStatus.buttonText}
                  </button>
                );
              })()}

            {onMarkAsResolved &&
              userInfoData?.ticket_account_type === "receptionist" && (
                <button
                  className=""
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkAsResolved(row);
                  }}
                  title={
                    row.status !== TICKET_WORK_COMPLETED
                      ? "Available when work is completed"
                      : "Mark as Resolved"
                  }
                  disabled={row.status !== TICKET_WORK_COMPLETED}
                  style={{
                    backgroundColor:
                      row.status === TICKET_WORK_COMPLETED
                        ? "#28a745"
                        : "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginLeft: "8px",
                    cursor:
                      row.status === TICKET_WORK_COMPLETED
                        ? "pointer"
                        : "not-allowed",
                    opacity: row.status === TICKET_WORK_COMPLETED ? 1 : 0.6,
                  }}
                >
                  Mark as Resolved
                </button>
              )}

            {/* Reject Button - Reusable component */}
            {renderRejectButton(row)}

            {/* Replace conditional rendering with always visible but conditionally disabled button */}
            {userInfoData?.ticket_account_type !== "supervisor" && (
              <button
                className=""
                onClick={(e) => {
                  e.stopPropagation();
                  handleReopenTicket(row);
                }}
                disabled={
                  !canReopenTicket(row, userInfoData?.ticket_account_type)
                }
                title={
                  canReopenTicket(row, userInfoData?.ticket_account_type)
                    ? "Reopen Ticket"
                    : "Cannot reopen this ticket"
                }
                style={{
                  backgroundColor: canReopenTicket(
                    row,
                    userInfoData?.ticket_account_type
                  )
                    ? "#f59e0b"
                    : "#e5e7eb",
                  color: canReopenTicket(row, userInfoData?.ticket_account_type)
                    ? "white"
                    : "#9ca3af",
                  border: "none",
                  borderRadius: "4px",
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "500",
                  marginLeft: "8px",
                  cursor: canReopenTicket(
                    row,
                    userInfoData?.ticket_account_type
                  )
                    ? "pointer"
                    : "not-allowed",
                  transition: "all 0.2s ease",
                }}
              >
                Reopen
              </button>
            )}
          </div>
        </div>
      ),
    },
  ];

  // Choose columns based on mode
  const tbColumns = isManagerAssignment
    ? managerAssignmentColumns
    : regularColumns;

  return (
    <div className="ticket-listing-container">
      {/* {console.log("tablePagination", tablePagination)} */}

      {/* Premium Table Container */}
      <div className="premium-table-container">
        {ticketsLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="black" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-4">
            <Alert variant="info">
              No tickets found for the selected filters
            </Alert>
          </div>
        ) : isMobile ? (
          // Mobile Card View
          <div className="mobile-tickets-container">
            {tickets.map((ticket) => (
              <MobileTicketCard
                key={ticket.uid}
                ticket={ticket}
                onTicketClick={onTicketClick}
                onVerifyClick={onVerifyClick}
                onMarkAsResolved={onMarkAsResolved}
                onReopenTicket={handleReopenTicket}
                onRejectTicket={handleRejectTicket}
                userInfoData={userInfoData}
                isManagerAssignment={isManagerAssignment}
                managerSupervisors={managerSupervisors}
                canUserRejectTicket={canUserRejectTicket}
                managerSupervisorsLoading={managerSupervisorsLoading}
                selectedAssignments={selectedAssignments}
                setSelectedAssignments={setSelectedAssignments}
                assigningTickets={assigningTickets}
                onAssignTicket={onAssignTicket}
                onTimelineClick={handleTimelineClick}
              />
            ))}

            {/* Mobile Pagination */}
            {tablePagination && tablePagination.length > 0 && (
              <div className="mobile-pagination-container">
                <div className="mobile-pagination-info">
                  <span className="pagination-text">
                    Showing {(isPage - 1) * pageLimit + 1} to{" "}
                    {Math.min(
                      isPage * pageLimit,
                      tablePagination[0]?.pagination || 0
                    )}{" "}
                    of {tablePagination[0]?.pagination || 0} tickets
                  </span>
                </div>
                <div className="mobile-pagination-controls">
                  <button
                    className="mobile-pagination-btn"
                    onClick={() => setIsPage(Math.max(1, isPage - 1))}
                    disabled={isPage <= 1}
                  >
                    Previous
                  </button>
                  <span className="mobile-page-indicator">
                    Page {isPage} of{" "}
                    {Math.ceil(
                      (tablePagination[0]?.pagination || 0) / pageLimit
                    )}
                  </span>
                  <button
                    className="mobile-pagination-btn"
                    onClick={() => setIsPage(isPage + 1)}
                    disabled={
                      isPage >=
                      Math.ceil(
                        (tablePagination[0]?.pagination || 0) / pageLimit
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View
          <PaymentLinkTable
            tableData={ticketsData}
            tableColumns={tbColumns}
            tablePagination={ticketsPagination}
            isPage={isPage}
            setIsPage={setIsPage}
            isFirstColSticky={true}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
          />
        )}
      </div>

      {/* Reopen Ticket Modal */}
      <ReopenTicketModal
        isOpen={isReopenModalOpen}
        onClose={handleReopenModalClose}
        ticket={selectedTicketForReopen}
        onSubmit={handleReopenSubmit}
        userInfoData={userInfoData}
        handleReload={handleReload}
      />

      {/* Reject Ticket Modal */}
      <RejectTicketModal
        isOpen={isRejectModalOpen}
        onClose={handleRejectModalClose}
        ticket={selectedTicketForReject}
        onSubmit={handleRejectSubmit}
        userInfoData={userInfoData}
        handleReload={handleReload}
      />

      {/* Handover Ticket Modal */}
      <HandoverTicketModal
        isOpen={isHandoverModalOpen}
        onClose={handleHandoverModalClose}
        ticket={selectedTicketForHandover}
        onSubmit={handleHandoverSubmit}
        handleReload={handleReload}
        userInfoData={userInfoData}
      />

      {/* Timeline Modal */}
      <TicketLifecycleTimeline
        show={isTimelineModalOpen}
        onHide={handleTimelineModalClose}
        ticketId={selectedTicketForTimeline?.uid}
        ticketInfo={selectedTicketForTimeline}
      />
    </div>
  );
};

export default TicketListing;

// Premium SLA Display Component for Desktop
export const PremiumSLADisplay = ({ ticket }) => {
  // Format status names
  const formatStatusName = (status) => {
    if (!status) return "N/A";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Convert string values to numbers and calculate allowed time
  const allowedTimeInMinutes =
    Number(ticket.allowed_sla_in_minutes) ||
    Number(ticket.allowed_days || "0") * 24 * 60 +
      Number(ticket.allowed_hours || "0") * 60 +
      Number(ticket.allowed_minutes || "0");

  const actualTimeInMinutes = Number(ticket.actual_minutes) || 0;
  const remainingMinutes = allowedTimeInMinutes - actualTimeInMinutes;
  const isBreached = Number(ticket.sla_breached) === 1 || remainingMinutes <= 0;

  const formatTime = (minutes) => {
    const totalMinutes = Math.abs(Math.floor(minutes || 0));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const mins = Math.floor(totalMinutes % 60);

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${mins}m`;
    } else {
      return `${mins}m`;
    }
  };

  const getSLAStatus = (ticket) => {
    // Use the database risk_level if available, otherwise calculate
    if (ticket.risk_level) {
      switch (ticket.risk_level) {
        case "BREACHED":
          return {
            level: "BREACHED",
            color: "#dc3545",
            bgColor: "#fef2f2",
            icon: "⚠️",
          };
        case "CRITICAL":
          return {
            level: "CRITICAL",
            color: "#dc2626",
            bgColor: "#fef2f2",
            icon: "🔴",
          };
        case "WARNING":
          return {
            level: "WARNING",
            color: "#f59e0b",
            bgColor: "#fefbf2",
            icon: "🟡",
          };
        case "CAUTION":
          return {
            level: "CAUTION",
            color: "#f59e0b",
            bgColor: "#fefbf2",
            icon: "🟡",
          };
        case "SAFE":
          return {
            level: "SAFE",
            color: "#10b981",
            bgColor: "#f0fdf4",
            icon: "🟢",
          };
        default:
          break;
      }
    }

    // Fallback calculation if risk_level is not available
    const allowedTimeInMinutes =
      Number(ticket.allowed_sla_in_minutes) ||
      Number(ticket.allowed_days || "0") * 24 * 60 +
        Number(ticket.allowed_hours || "0") * 60 +
        Number(ticket.allowed_minutes || "0");

    const actualTimeInMinutes = Number(ticket.actual_minutes) || 0;
    const remainingMinutes = allowedTimeInMinutes - actualTimeInMinutes;

    if (Number(ticket.sla_breached) === 1 || remainingMinutes <= 0) {
      return {
        level: "BREACHED",
        color: "#dc3545",
        bgColor: "#fef2f2",
        icon: "⚠️",
      };
    }

    const percentageRemaining = Math.max(
      0,
      remainingMinutes / allowedTimeInMinutes
    );

    if (percentageRemaining >= 0.7) {
      return {
        level: "SAFE",
        color: "#10b981",
        bgColor: "#f0fdf4",
        icon: "🟢",
      };
    } else if (percentageRemaining >= 0.3) {
      return {
        level: "CAUTION",
        color: "#f59e0b",
        bgColor: "#fefbf2",
        icon: "🟡",
      };
    } else if (percentageRemaining >= 0.1) {
      return {
        level: "WARNING",
        color: "#f59e0b",
        bgColor: "#fefbf2",
        icon: "🟡",
      };
    } else if (percentageRemaining > 0) {
      return {
        level: "CRITICAL",
        color: "#dc2626",
        bgColor: "#fef2f2",
        icon: "🔴",
      };
    } else {
      // Only show BREACHED when actually breached
      return {
        level: "BREACHED",
        color: "#dc3545",
        bgColor: "#fef2f2",
        icon: "⚠️",
      };
    }
  };

  const slaStatus = getSLAStatus(ticket);

  if (!allowedTimeInMinutes) {
    return (
      <div
        className="sla-display-container"
        style={{
          padding: "8px 10px",
          textAlign: "center",
          color: "#6b7280",
          fontSize: "11px",
          backgroundColor: "#f9fafb",
          borderRadius: "6px",
          border: "1px solid #e5e7eb",
          minHeight: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ fontWeight: "500" }}>No SLA</div>
      </div>
    );
  }

  return (
    <div
      className="premium-sla-display"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        padding: "8px 10px",
        backgroundColor: slaStatus.bgColor,
        border: `1px solid ${slaStatus.color}30`,
        borderRadius: "6px",
        minWidth: "130px",
        position: "relative",
        cursor: "help",
        height: "fit-content",
        minHeight: "60px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: "12px" }}>{slaStatus.icon}</span>

        <div
          style={{
            fontSize: "12px",
            fontWeight: "700",
            color: slaStatus.color,
            textAlign: "center",
            lineHeight: "1.1",
          }}
        >
          {isBreached ? (
            <span>+{formatTime(Math.abs(remainingMinutes))}</span>
          ) : (
            <span>{formatTime(Math.max(0, remainingMinutes))}</span>
          )}
        </div>
      </div>

      <div
        style={{
          fontSize: "10px",
          color: slaStatus.color,
          textAlign: "center",
          fontWeight: "600",
          letterSpacing: "0.3px",
          display: "flex",
          flexDirection: "column",
          gap: "1px",
        }}
      >
        <div
          style={{
            fontSize: "9px",
            color: "#6b7280",
            fontWeight: "500",
            letterSpacing: "0.2px",
          }}
        >
          {isBreached ? (
            <>
              Breached at &nbsp;
              <b style={{ textTransform: "capitalize" }}>
                ({formatStatusName(ticket.next_status)})
              </b>
              &nbsp;status
            </>
          ) : (
            <>
              {formatTime(Math.max(0, remainingMinutes))} remaining until &nbsp;
              <b style={{ textTransform: "capitalize" }}>
                {formatStatusName(ticket.next_status)}
              </b>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          height: "3px",
          backgroundColor: `${slaStatus.color}20`,
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            backgroundColor: slaStatus.color,
            width: isBreached
              ? "100%"
              : `${Math.min(
                  100,
                  (actualTimeInMinutes / allowedTimeInMinutes) * 100
                )}%`,
            transition: "width 0.3s ease",
            borderRadius: "2px",
          }}
        />
      </div>
    </div>
  );
};

// Compact SLA Component for Mobile
const CompactSLADisplay = ({ ticket }) => {
  const allowedTimeInMinutes =
    ticket.allowed_sla_in_minutes ||
    parseInt(ticket.allowed_days || 0) * 24 * 60 +
      parseInt(ticket.allowed_hours || 0) * 60 +
      parseInt(ticket.allowed_minutes || 0);

  const actualTimeInMinutes = Number(ticket.actual_minutes) || 0;
  const remainingMinutes = allowedTimeInMinutes - actualTimeInMinutes;
  const isBreached = Number(ticket.sla_breached) === 1 || remainingMinutes <= 0;

  // Format status names
  const formatStatusName = (status) => {
    if (!status) return "N/A";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatTime = (minutes) => {
    const totalMinutes = Math.abs(Math.floor(minutes || 0));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const mins = Math.floor(totalMinutes % 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getSLAColor = (minutes) => {
    if (minutes <= 0) return "#dc3545";
    if (minutes <= 60) return "#dc2626";
    if (minutes <= 240) return "#f59e0b";
    return "#10b981";
  };

  const getSLAIcon = (minutes) => {
    if (minutes <= 0) return "⚠️";
    if (minutes <= 60) return "🔴";
    if (minutes <= 240) return "🟡";
    return "🟢";
  };

  if (!allowedTimeInMinutes) return null;

  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "2px",
        padding: "4px 8px",
        borderRadius: "6px",
        backgroundColor: isBreached ? "#fef2f2" : "#f0fdf4",
        border: `1px solid ${getSLAColor(remainingMinutes)}30`,
        fontSize: "11px",
        fontWeight: "700",
        color: getSLAColor(remainingMinutes),
        minWidth: "80px",
        justifyContent: "center",
        cursor: "help",
        position: "relative",
      }}
      title={`Current: ${formatStatusName(ticket.status)}${
        ticket.next_status
          ? ` → Next: ${formatStatusName(ticket.next_status)}`
          : ""
      } | SLA: ${isBreached ? "Breached by" : "Remaining"} ${formatTime(
        Math.abs(remainingMinutes)
      )}`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span style={{ fontSize: "10px" }}>{getSLAIcon(remainingMinutes)}</span>
        <span>
          {isBreached
            ? `+${formatTime(Math.abs(remainingMinutes))}`
            : formatTime(Math.max(0, remainingMinutes))}
        </span>
      </div>
      <div
        style={{
          fontSize: "8px",
          color: "#6b7280",
          fontWeight: "500",
          textTransform: "capitalize",
        }}
      >
        {formatStatusName(ticket.status)}
      </div>
    </div>
  );
};
